// C:\Users\QI\komondi-dashboard\src\pages\UserTypePage.js
import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { ref, onValue, set, update, get } from "firebase/database";
import { useParams, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { useQuery, useQueries, QueryClient, QueryClientProvider } from "@tanstack/react-query";

// إنشاء عميل الاستعلامات - يجب وضعه خارج المكون
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // 5 دقائق - البيانات تعتبر قديمة بعد 5 دقائق
      cacheTime: 30 * 60 * 1000,      // 30 دقيقة - احتفظ بالكاش في الذاكرة
      refetchOnWindowFocus: false,    // لا تعيد الجلب عند العودة للنافذة
      refetchOnMount: false,          // لا تعيد الجلب عند تحميل المكون (الأهم!)
      refetchOnReconnect: false,      // لا تعيد الجلب عند إعادة الاتصال
      retry: 1,
      keepPreviousData: true,         // احتفظ بالبيانات السابقة أثناء جلب جديدة
    },
  },
});

// هوك مخصص لجلب عدد الطلبيات
const useOrdersCount = (userIds) => {
  const queries = useQueries({
    queries: userIds.map((userId) => ({
      queryKey: ["orderCount", userId],
      queryFn: async () => {
        try {
          const distributorRef = ref(db, `distributors/${userId}/orderCounter`);
          const snapshot = await get(distributorRef);
          return { userId, count: snapshot.exists() ? snapshot.val() : 0 };
        } catch (error) {
          console.error(`Error fetching order count for user ${userId}:`, error);
          return { userId, count: 0 };
        }
      },
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    })),
  });

  const counts = {};
  queries.forEach((query) => {
    if (query.data) {
      counts[query.data.userId] = query.data.count;
    }
  });

  return {
    counts,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
};

// هوك مخصص لجلب عدد المنتجات 
const useProductsCount = (userIds) => {
  const queries = useQueries({
    queries: userIds.map((userId) => ({
      queryKey: ["productsCount", userId],
      queryFn: async () => {
        try {
          // نقرأ من عقدة products مباشرة (بدون productsCounter)
          const productsRef = ref(db, `distributors/${userId}/products`);
          const snapshot = await get(productsRef);
          const products = snapshot.val();
          const count = products ? Object.keys(products).length : 0;
          return { userId, count };
        } catch (error) {
          console.error(`Error fetching products count for user ${userId}:`, error);
          return { userId, count: 0 };
        }
      },
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    })),
  });

  const counts = {};
  queries.forEach((query) => {
    if (query.data) {
      counts[query.data.userId] = query.data.count;
    }
  });

  return {
    counts,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
};

// مكون داخلي يستخدم الـ hooks
function UserTypePageContent() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [updatingAccountType, setUpdatingAccountType] = useState({});
  const [updatingRegistrationDate, setUpdatingRegistrationDate] = useState(false);
  const [clientActivities, setClientActivities] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [allActivities, setAllActivities] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    withRegistrationDate: 0,
    withoutRegistrationDate: 0
  });

  // استخدام React Query لجلب عدد الطلبيات
  const userIds = useMemo(() => users.map(u => u.id), [users]);
  const { counts: ordersCount, isLoading: loadingOrders } = useOrdersCount(
    type?.toLowerCase() === "professional" ? userIds : []
  );
// استخدام React Query لجلب عدد المنتجات
  const { counts: productsCount, isLoading: loadingProducts } = useProductsCount(
  type?.toLowerCase() === "professional" ? userIds : []
);

  const seedClientActivities = async () => {
    const activities = {
      ca01: {
        id: "ca01",
        name: "Cosmétiques",
        imageUrl: ""
      },
      ca02: {
        id: "ca02",
        name: "Supermarché",
        imageUrl: ""
      },
      ca03: {
        id: "ca03",
        name: "Informatique & Bureautique",
        imageUrl: ""
      },
      ca04: {
        id: "ca04",
        name: "Fournitures de Bureau",
        imageUrl: ""
      }
    };

    try {
      await set(ref(db, "client_activities"), activities);
      alert("✅ client_activities created successfully");
    } catch (e) {
      console.error(e);
      alert("❌ error creating client_activities");
    }
  };

  const normalize = (str) => str?.toLowerCase().trim();

  useEffect(() => {
    const actRef = ref(db, "client_activities");
    const unsub = onValue(actRef, (snap) => {
      const data = snap.val() || {};
      const arr = Object.values(data);
      setAllActivities(arr);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const usersRef = ref(db, "users");
    const unsub = onValue(usersRef, (snap) => {
      const val = snap.val();
      if (!val) {
        setUsers([]);
        setStats({ total: 0, withRegistrationDate: 0, withoutRegistrationDate: 0 });
        return;
      }
      
      const filteredUsers = Object.keys(val)
        .map((key) => ({
          id: key,
          ...val[key],
        }))
        .filter((u) => 
          u.userType && 
          u.userType.toLowerCase() === type?.toLowerCase()
        );
      
      setUsers(filteredUsers);
      
      const withDate = filteredUsers.filter(u => u.registrationDate).length;
      const withoutDate = filteredUsers.length - withDate;
      setStats({
        total: filteredUsers.length,
        withRegistrationDate: withDate,
        withoutRegistrationDate: withoutDate
      });
    });
    return () => unsub();
  }, [type]);

  const exportToExcel = () => {
    if (filteredUsers.length === 0) {
      alert("No users to export");
      return;
    }

    const data = filteredUsers.map((u) => ({
      "Trade Name": u.tradeName || u.name || "",
      "Email": u.email || "",
      "Phone": u.phone || "",
      "User Type": u.userType || "",
      "Account Type": u.accountType || "",
      "Registration Date": u.registrationDate || "",
      "Number of Orders": type?.toLowerCase() === "professional" ? (ordersCount[u.id] || 0) : "",
      "Active": u.active !== false ? "Yes" : "No",
      "Address": u.adresse || ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    XLSX.writeFile(
      workbook,
      `${type}_users_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const migrateClientActivity = async (user) => {
    try {
      if (!user.activityName) {
        alert("User has no activity");
        return;
      }

      const newId = clientActivities[normalize(user.activityName)];

      if (!newId) {
        alert("No matching client activity found");
        return;
      }

      const userRef = ref(db, `users/${user.id}`);

      await update(userRef, {
        clientActivityId: newId,
        clientActivityName: user.activityName,
        activityId: null,
        activityName: null
      });

      alert("✅ Activity migrated successfully");

    } catch (err) {
      console.error(err);
      alert("Migration failed");
    }
  };

  const updateAccountType = async (userId, newAccountType) => {
    setUpdatingAccountType(prev => ({ ...prev, [userId]: true }));
    try {
      const userRef = ref(db, `users/${userId}`);
      await update(userRef, {
        accountType: newAccountType
      });
      
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId 
            ? { ...u, accountType: newAccountType } 
            : u
        )
      );
      
      alert(`Account type updated to ${newAccountType} successfully!`);
    } catch (error) {
      console.error("Error updating account type:", error);
      alert("Failed to update account type");
    } finally {
      setUpdatingAccountType(prev => ({ ...prev, [userId]: false }));
    }
  };

  const updateOldUsersRegistrationDate = async () => {
    if (!window.confirm(`This will update registration date for all ${type} users without a registration date. Continue?`)) {
      return;
    }
    
    setUpdatingRegistrationDate(true);
    
    try {
      const usersToUpdate = users.filter(u => !u.registrationDate);
      const updates = {};
      const currentDate = new Date().toISOString();
      
      usersToUpdate.forEach(user => {
        const registrationDate = user.createdAt 
          ? new Date(user.createdAt).toISOString()
          : currentDate;
        
        updates[`${user.id}/registrationDate`] = registrationDate;
        updates[`${user.id}/accountType`] = user.accountType || "public";
      });
      
      if (Object.keys(updates).length === 0) {
        alert("All users already have registration date!");
        setUpdatingRegistrationDate(false);
        return;
      }
      
      const usersRef = ref(db, "users");
      await update(usersRef, updates);
      
      alert(`Successfully updated ${usersToUpdate.length} users!`);
      
      setUsers(prevUsers => 
        prevUsers.map(u => 
          !u.registrationDate 
            ? { 
                ...u, 
                registrationDate: u.createdAt ? new Date(u.createdAt).toISOString() : new Date().toISOString(),
                accountType: u.accountType || "public"
              } 
            : u
        )
      );
      
    } catch (error) {
      console.error("Error updating registration dates:", error);
      alert("Failed to update registration dates: " + error.message);
    } finally {
      setUpdatingRegistrationDate(false);
    }
  };

  const updateSingleUserRegistrationDate = async (userId, currentDate) => {
    try {
      const newDate = currentDate || new Date().toISOString();
      const userRef = ref(db, `users/${userId}`);
      await update(userRef, {
        registrationDate: newDate,
        accountType: "public"
      });
      
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId 
            ? { ...u, registrationDate: newDate, accountType: "public" } 
            : u
        )
      );
      
      alert("Registration date updated successfully!");
    } catch (error) {
      console.error("Error updating registration date:", error);
      alert("Failed to update registration date");
    }
  };

  const filteredUsers = useMemo(() => {
  const parseDate = (val) => {
    if (!val) return 0;
    return new Date(val.replace("\n", "T")).getTime();
  };

  // إذا كان type هو professional، رتب حسب عدد الطلبيات
  if (type?.toLowerCase() === "professional") {
    return users
      .filter((u) => {
        if (!filterDate) return true;
        return u.registrationDate && u.registrationDate.startsWith(filterDate);
      })
      .sort((a, b) => {
        // الترتيب حسب عدد الطلبيات (من الأكبر إلى الأصغر)
        const ordersA = ordersCount[a.id] || 0;
        const ordersB = ordersCount[b.id] || 0;
        
        if (ordersB !== ordersA) {
          return ordersB - ordersA; // ترتيب تنازلي حسب الطلبيات
        }
        
        // إذا تساوت الطلبيات، رتب حسب تاريخ التسجيل (الأحدث أولاً)
        return parseDate(b.registrationDate) - parseDate(a.registrationDate);
      });
  }
  
  // لبقية أنواع المستخدمين (client, vendeur, admin) رتب حسب التاريخ فقط
  return users
    .filter((u) => {
      if (!filterDate) return true;
      return u.registrationDate && u.registrationDate.startsWith(filterDate);
    })
    .sort((a, b) => {
      return parseDate(b.registrationDate) - parseDate(a.registrationDate);
    });
  }, [users, filterDate, type, ordersCount]); 

  const formatDate = (dateString) => {
    if (!dateString) return (
      <span style={{ color: "#ef4444", fontStyle: "italic" }}>
        No date
      </span>
    );
    
    try {
      const d = new Date(dateString);
      if (isNaN(d)) return dateString;
      
      return (
        <div>
          <div>{d.toISOString().slice(0, 10)}</div>
          <div style={{ fontSize: "11px", color: "#6b7280" }}>
            {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      );
    } catch {
      return dateString;
    }
  };

  const getTypeColor = () => {
    switch (type?.toLowerCase()) {
      case "client": return "#4f46e5";
      case "professional": return "#0891b2";
      case "vendeur": return "#16a34a";
      case "admin": return "#b91c1c";
      default: return "#6b7280";
    }
  };

  const renderAccountTypeCell = (user) => {
    if (type?.toLowerCase() !== "professional") {
      return "-";
    }
    
    const isPublic = user.accountType === "public";
    
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ 
          color: isPublic ? "#16a34a" : "#dc2626",
          fontWeight: "bold",
          fontSize: "13px"
        }}>
          {user.accountType || "public"}
        </span>
        <button
          onClick={() => updateAccountType(
            user.id, 
            isPublic ? "privé" : "public"
          )}
          disabled={updatingAccountType[user.id]}
          style={{
            padding: "4px 8px",
            background: isPublic ? "#dc2626" : "#16a34a",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: "11px",
            fontWeight: "500",
            minWidth: 70,
            opacity: updatingAccountType[user.id] ? 0.6 : 1
          }}
        >
          {updatingAccountType[user.id] 
            ? "..." 
            : isPublic ? "Private" : "Public"
          }
        </button>
      </div>
    );
  };

  const th = { textAlign: "left", padding: 12, fontSize: "14px" };
  const td = { padding: 12, fontSize: "14px", verticalAlign: "middle" };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 15, marginBottom: 20 }}>
        <button
          onClick={() => navigate("/")}
          style={{
            padding: "8px 16px",
            background: "#e5e7eb",
            border: "none",
            borderRadius: 6,
            cursor: "pointer"
          }}
        >
          ← Back to Dashboard
        </button>
        <h3 style={{ margin: 0, color: getTypeColor() }}>
          {type?.charAt(0).toUpperCase() + type?.slice(1)} Users ({filteredUsers.length})
        </h3>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          style={{ 
            padding: 8, 
            border: "1px solid #d1d5db", 
            borderRadius: 6,
            fontSize: "14px"
          }}
        />
        <button
          onClick={exportToExcel}
          disabled={filteredUsers.length === 0}
          style={{
            padding: "8px 16px",
            background: filteredUsers.length === 0 ? "#9ca3af" : "#10b981",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: filteredUsers.length === 0 ? "not-allowed" : "pointer",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
        >
          📊 Export to Excel
        </button>

        <button 
          onClick={() => setFilterDate("")}
          style={{
            padding: "8px 16px",
            background: "#6b7280",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: "14px"
          }}
        >
          Clear Date Filter
        </button>
        <button
          onClick={seedClientActivities}
          style={{
            padding: "10px 14px",
            background: "#10b981",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer"
          }}
        >
          Create Client Activities
        </button>
        
        {stats.withoutRegistrationDate > 0 && (
          <button
            onClick={updateOldUsersRegistrationDate}
            disabled={updatingRegistrationDate}
            style={{
              padding: "8px 16px",
              background: "#f59e0b",
              color: "white",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              opacity: updatingRegistrationDate ? 0.6 : 1
            }}
          >
            {updatingRegistrationDate ? "⏳ Updating..." : "🔄 Update Old Users"}
            <span style={{ 
              background: "#dc2626", 
              color: "white",
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px"
            }}>
              {stats.withoutRegistrationDate}
            </span>
          </button>
        )}
        
        {type?.toLowerCase() === "professional" && (
          <div style={{ 
            display: "flex", 
            gap: 15, 
            marginLeft: "auto",
            alignItems: "center",
            flexWrap: "wrap"
          }}>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ 
                padding: "6px 12px", 
                background: "#16a34a20", 
                borderRadius: 6,
                border: "1px solid #16a34a40"
              }}>
                <span style={{ color: "#16a34a", fontWeight: "bold" }}>
                  Public: {users.filter(u => u.accountType === "public").length}
                </span>
              </div>
              <div style={{ 
                padding: "6px 12px", 
                background: "#dc262620", 
                borderRadius: 6,
                border: "1px solid #dc262640"
              }}>
                <span style={{ color: "#dc2626", fontWeight: "bold" }}>
                  Private: {users.filter(u => u.accountType === "privé").length}
                </span>
              </div>
            </div>
            
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ 
                padding: "6px 12px", 
                background: "#3b82f620", 
                borderRadius: 6,
                border: "1px solid #3b82f640"
              }}>
                <span style={{ color: "#3b82f6", fontWeight: "bold" }}>
                  📦 Total Orders: {Object.values(ordersCount).reduce((sum, count) => sum + (count || 0), 0)}
                  {loadingOrders && " (loading...)"}
                </span>
              </div>
              <div style={{ 
                padding: "6px 12px", 
                background: "#f59e0b20", 
                borderRadius: 6,
                border: "1px solid #f59e0b40"
              }}>
                <span style={{ color: "#f59e0b", fontWeight: "bold" }}>
                  🏪 Active: {Object.values(ordersCount).filter(count => count > 0).length}/{users.length}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ 
        display: "flex", 
        gap: 15, 
        marginBottom: 20,
        background: "#f9fafb",
        padding: 15,
        borderRadius: 8,
        flexWrap: "wrap"
      }}>
        <div style={{ 
          padding: "10px 15px", 
          background: "#3b82f6", 
          color: "white",
          borderRadius: 6,
          minWidth: 120
        }}>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>Total Users</div>
          <div style={{ fontSize: 24, fontWeight: "bold" }}>{stats.total}</div>
        </div>
        
        <div style={{ 
          padding: "10px 15px", 
          background: "#10b981", 
          color: "white",
          borderRadius: 6,
          minWidth: 120
        }}>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>With Date</div>
          <div style={{ fontSize: 24, fontWeight: "bold" }}>{stats.withRegistrationDate}</div>
        </div>
        
        <div style={{ 
          padding: "10px 15px", 
          background: stats.withoutRegistrationDate > 0 ? "#ef4444" : "#6b7280", 
          color: "white",
          borderRadius: 6,
          minWidth: 120
        }}>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>Without Date</div>
          <div style={{ fontSize: 24, fontWeight: "bold" }}>{stats.withoutRegistrationDate}</div>
        </div>

        {type?.toLowerCase() === "professional" && (
          <>
            <div style={{ 
              padding: "10px 15px", 
              background: "#8b5cf6", 
              color: "white",
              borderRadius: 6,
              minWidth: 120
            }}>
              <div style={{ fontSize: "12px", opacity: 0.9 }}>Total Orders</div>
              <div style={{ fontSize: 24, fontWeight: "bold" }}>
                {Object.values(ordersCount).reduce((sum, count) => sum + (count || 0), 0)}
              </div>
            </div>
            
            <div style={{ 
              padding: "10px 15px", 
              background: "#ec4899", 
              color: "white",
              borderRadius: 6,
              minWidth: 120
            }}>
              <div style={{ fontSize: "12px", opacity: 0.9 }}>Average Orders</div>
              <div style={{ fontSize: 24, fontWeight: "bold" }}>
                {users.length > 0 
                  ? Math.round(Object.values(ordersCount).reduce((sum, count) => sum + (count || 0), 0) / users.length) 
                  : 0}
              </div>
            </div>
           <div style={{ 
      padding: "10px 15px", 
      background: "#a855f7", 
      color: "white",
      borderRadius: 6,
      minWidth: 120
    }}>
      <div style={{ fontSize: "12px", opacity: 0.9 }}>Total Products</div>
      <div style={{ fontSize: 24, fontWeight: "bold" }}>
        {Object.values(productsCount).reduce((sum, count) => sum + (count || 0), 0)}
      </div>
    </div>
    
    <div style={{ 
      padding: "10px 15px", 
      background: "#d946ef", 
      color: "white",
      borderRadius: 6,
      minWidth: 120
    }}>
      <div style={{ fontSize: "12px", opacity: 0.9 }}>Avg Products/Distributor</div>
      <div style={{ fontSize: 24, fontWeight: "bold" }}>
        {users.length > 0 
          ? Math.round(Object.values(productsCount).reduce((sum, count) => sum + (count || 0), 0) / users.length) 
          : 0}
      </div>
    </div>
      
          </>
        )}
      </div>

    <div style={{ 
  overflowX: "auto",
  overflowY: "auto",  // غير visible إلى auto
  WebkitOverflowScrolling: "touch",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  background: "white",
  maxHeight: "calc(100vh - 250px)",  // ارتفاع ثابت (شاشة كاملة - مساحة الهيدر)
  height: "auto"
}}>
  <table style={{ 
    width: "2000px",  // عرض ثابت كبير
    borderCollapse: "collapse", 
    whiteSpace: "nowrap"
  }}>
          <thead>
            <tr style={{ background: "#f3f4f6" }}>
              <th style={th}>Image</th>
              <th style={th}>Trade Name</th>
              <th style={th}>Email</th>
              <th style={th}>Phone</th>
              <th style={th}>
                <div>Registration Date</div>
                <div style={{ fontSize: "11px", fontWeight: "normal" }}>
                  Click to update
                </div>
              </th>
              {type?.toLowerCase() === "professional" && (
                <>
              <th style={th}>Account Type</th>
              <th style={th}>
             <div>📦 Number of Orders</div>
            <div style={{ fontSize: "11px", fontWeight: "normal", color: "#6b7280" }}>
        {loadingOrders && "⏳ Loading..."}
      </div>
    </th>
    <th style={th}>  {/* أضف هذا العمود الجديد */}
      <div>📊 Products Count</div>
      <div style={{ fontSize: "11px", fontWeight: "normal", color: "#6b7280" }}>
        {loadingProducts && "⏳ Loading..."}
      </div>
    </th>
  </>
)}
              <th style={th}>Address</th>
              <th style={th}>Active</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id} style={{ 
                borderBottom: "1px solid #e5e7eb",
                background: !u.registrationDate ? "#fef2f2" : 
                           u.accountType === "privé" ? "#fef2f2" : "white"
              }}>
                <td style={td}>
                  <img
                    src={u.imageUrl || "https://via.placeholder.com/40"}
                    alt=""
                    width={40}
                    height={40}
                    style={{ borderRadius: "50%" }}
                  />
                </td>
                <td style={td}>
                  <strong>{u.tradeName || u.name || "-"}</strong>
                  {(u.clientActivityName || u.activityName) && (
                    <div style={{ fontSize: "12px", color: "#6b7280", marginTop: 4 }}>
                      {u.clientActivityName || u.activityName}
                    </div>
                  )}
                </td>
                <td style={td}>
                  <div style={{ fontSize: "13px" }}>{u.email || "-"}</div>
                </td>
                <td style={td}>{u.phone || "-"}</td>
                <td style={td}>
                  <div style={{ cursor: "pointer" }} onClick={() => updateSingleUserRegistrationDate(u.id)}>
                    {formatDate(u.registrationDate)}
                  </div>
                  {!u.registrationDate && (
                    <button
                      onClick={() => updateSingleUserRegistrationDate(u.id)}
                      style={{
                        marginTop: 5,
                        padding: "4px 8px",
                        background: "#f59e0b",
                        color: "white",
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontSize: "11px"
                      }}
                    >
                      Set Date
                    </button>
                  )}
                </td>
                
                {type?.toLowerCase() === "professional" && (
  <>
    <td style={td}>
      {renderAccountTypeCell(u)}
    </td>
    <td style={td}>
      <div style={{
        background: ordersCount[u.id] ? "#3b82f6" : "#9ca3af",
        color: "white",
        padding: "6px 12px",
        borderRadius: "20px",
        fontSize: "16px",
        fontWeight: "bold",
        display: "inline-block",
        minWidth: "50px",
        textAlign: "center"
      }}>
        {loadingOrders ? "..." : (ordersCount[u.id] || 0)}
      </div>
      {ordersCount[u.id] > 0 && (
        <div style={{ 
          fontSize: "11px", 
          color: "#3b82f6", 
          marginTop: 4,
          fontWeight: "500"
        }}>
          ⭐ Active distributor
        </div>
      )}
    </td>
   
    <td style={td}>
      <div style={{
        background: productsCount[u.id] ? "#8b5cf6" : "#9ca3af",
        color: "white",
        padding: "6px 12px",
        borderRadius: "20px",
        fontSize: "16px",
        fontWeight: "bold",
        display: "inline-block",
        minWidth: "50px",
        textAlign: "center"
      }}>
        {loadingProducts ? "..." : (productsCount[u.id] || 0)}
      </div>
      {productsCount[u.id] > 0 && (
        <div style={{ 
          fontSize: "11px", 
          color: "#8b5cf6", 
          marginTop: 4,
          fontWeight: "500"
        }}>
          📦 Products available
        </div>
      )}
    </td>
  </>
)}
                
                <td style={td}>{u.adresse || "-"}</td>
                <td style={td}>
                  <input
                    type="checkbox"
                    checked={u.active !== false}
                    onChange={async (e) => {
                      try {
                        const userRef = ref(db, `users/${u.id}/active`);
                        await set(userRef, e.target.checked);
                      } catch (err) {
                        console.error("Error updating active:", err);
                      }
                    }}
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                  />
                </td>
                <td style={td}>
                  <button
                    onClick={() => navigate(`/user/${u.id}`)}
                    style={{
                      padding: "6px 12px",
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: "500"
                    }}
                  >
                    View Details
                  </button>

                  {type?.toLowerCase() === "client" && (
                    <button
                      onClick={() => setEditingUser(u)}
                      style={{
                        marginLeft: 8,
                        padding: "6px 12px",
                        background: "#f59e0b",
                        color: "white",
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontSize: "12px"
                      }}
                    >
                      ✏️ Change Activity
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredUsers.length === 0 && (
        <div style={{ 
          textAlign: "center", 
          padding: 60,
          background: "#f9fafb",
          borderRadius: 8,
          marginTop: 20
        }}>
          <div style={{ fontSize: 48, marginBottom: 20, opacity: 0.3 }}>👤</div>
          <p style={{ fontSize: "16px", color: "#6b7280", marginBottom: 10 }}>
            No {type} users found.
          </p>
          <button
            onClick={() => setFilterDate("")}
            style={{
              padding: "8px 16px",
              background: getTypeColor(),
              color: "white",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Clear Filters
          </button>
          <button
            onClick={exportToExcel}
            style={{
              marginLeft: 10,
              padding: "8px 16px",
              background: "#10b981",
              color: "white",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: "14px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6
            }}
          >
            📊 Export to Excel
          </button>
        </div>
      )}
      
      {editingUser && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 999
        }}>
          <div style={{
            background: "white",
            padding: 20,
            borderRadius: 8,
            width: 400
          }}>
            <h3>Select Client Activity</h3>
            {allActivities.map(act => (
              <div key={act.id} style={{ marginBottom: 8 }}>
                <button
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 6,
                    border: "1px solid #ddd",
                    cursor: "pointer"
                  }}
                  onClick={async () => {
                    await update(ref(db, `users/${editingUser.id}`), {
                      clientActivityId: act.id,
                      clientActivityName: act.name,
                      activityId: null,
                      activityName: null
                    });
                    setEditingUser(null);
                    alert("✅ Activity updated");
                  }}
                >
                  {act.name}
                </button>
              </div>
            ))}
            <button
              onClick={() => setEditingUser(null)}
              style={{ marginTop: 10 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// المكون الرئيسي المغلف بـ QueryClientProvider
export default function UserTypePage() {
  return (
    <QueryClientProvider client={queryClient}>
      <UserTypePageContent />
    </QueryClientProvider>
  );
}